import * as React from 'react';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';

import InstrumentCard from './elements/instruments-card';
import InstrumentNotAddedCard from './elements/instruments-not-added-card';
import AddInstrumentDialog from './elements/add-instrument-dialog';
import { Instruments } from '@/shared/i18n';
import { EventBus } from '@/shared/utils';
import { InstrumentsEvents } from '../events';
import {
  InstrumentArraySchema,
  ProfileSkillLevelsArraySchema,
  SkillLevelsArraySchema,
} from '../types';
import { useInstrumentsForm } from '../hooks/use-instruments-form';
import DeleteConfirmationDialog from './elements/delete-confirmation-dialog';

interface InstrumentsFormProps {
  eventBus: EventBus<InstrumentsEvents>;
  dict: Instruments;
  instruments: InstrumentArraySchema;
  profileSkillLevels: ProfileSkillLevelsArraySchema;
  skillLevelsOptions: SkillLevelsArraySchema;
}

const InstrumentsForm: React.FC<InstrumentsFormProps> = ({
  instruments,
  profileSkillLevels,
  skillLevelsOptions,
  dict,
  eventBus,
}) => {
  const {
    addedInstruments,
    removing,
    handleDeleteInstrument,
    notAddedInstruments,
    handleCloseDialog,
    handleOpenDialog,
    selectedInstrument,
    selectedSkillLevel,
    deletingSkillLevel,
    handleInstrument,
    isUpdating,
  } = useInstrumentsForm({ instruments, profileSkillLevels, eventBus });

  return (
    <>
      {/* Render added instruments */}
      <Box sx={{ mt: 2 }}>
        <section className="flex flex-col gap-2">
          {addedInstruments.map((instrument) => (
            <div
              key={instrument.name}
              className={`${removing === instrument.name ? 'animate-shrink' : 'animate-grow'}`}
            >
              <InstrumentCard
                dict={dict}
                imageSrc={instrument.imageSrc}
                name={instrument.name}
                skillLevel={instrument.skillLevel}
                onDelete={() =>
                  handleOpenDialog(
                    {
                      imageSrc: instrument.imageSrc,
                      name: instrument.name,
                      skillLevel: instrument.skillLevel,
                    },
                    { isDeleting: true },
                  )
                }
                onUpdate={() =>
                  handleOpenDialog(
                    {
                      imageSrc: instrument.imageSrc,
                      name: instrument.name,
                      skillLevel: instrument.skillLevel,
                    },
                    { isUpdating: true },
                  )
                }
                skillLevelsOptions={skillLevelsOptions}
              />
            </div>
          ))}
        </section>
      </Box>
      <Typography variant="h4" className="mb-4 text-center">
        {dict.instruments.instrument_section_title}
      </Typography>
      {/* Not Added Instruments */}
      <Grid container spacing={{ xs: 1, md: 2 }}>
        {notAddedInstruments.map((instrument) => (
          <Grid size={{ xs: 6, md: 4, lg: 3 }} key={instrument.name}>
            <InstrumentNotAddedCard
              imageSrc={instrument.imageSrc}
              title={instrument.name}
              onAdd={() => handleOpenDialog(instrument)}
              dict={dict}
            />
          </Grid>
        ))}
      </Grid>
      <AddInstrumentDialog
        dict={dict}
        open={!!selectedInstrument}
        onClose={handleCloseDialog}
        onConfirm={handleInstrument}
        imageSrc={selectedInstrument?.imageSrc}
        name={selectedInstrument?.name}
        skillLevelsOptions={skillLevelsOptions}
        selectedOption={selectedSkillLevel}
        isUpdating={isUpdating}
      />
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        dict={dict}
        open={!!deletingSkillLevel}
        imageSrc={deletingSkillLevel?.instrument.imageSrc}
        name={deletingSkillLevel?.instrument.name}
        onClose={handleCloseDialog}
        onConfirm={() => {
          if (deletingSkillLevel) handleDeleteInstrument();
        }}
      />
    </>
  );
};

export { InstrumentsForm };
